import type { ApolloCache } from '@apollo/client';
import type {
  CompleteMembershipFragment,
  GetSuggestedMembersQuery,
  GetSuggestedMembersQueryVariables,
} from '@frameio/graphql-client/generated';
import { GetSuggestedMembers } from '@frameio/graphql-client/generated';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { updateQuery } from '../updateQuery';

type SuggestedAccountUser = Extract<
  NonNullable<GetSuggestedMembersQuery['suggestedMembers']>['nodes'][number],
  { __typename?: 'SuggestedAccountUser' }
>;

const updateSuggestedAccountUsersData = (
  memberships: Array<CompleteMembershipFragment | null>,
  existingData: GetSuggestedMembersQuery | null
): GetSuggestedMembersQuery | null => {
  if (!existingData?.suggestedMembers?.nodes) return existingData;

  const nodes: Map<string, SuggestedAccountUser> = new Map(
    (function* collectSuggestedUsers() {
      for (const node of existingData?.suggestedMembers?.nodes ?? []) {
        if (node.__typename === 'SuggestedAccountUser') {
          yield [node.id, node as SuggestedAccountUser];
        }
      }
    })()
  );

  const netNew = new Set<string>();

  memberships.forEach((membership) => {
    const accountUser = membership?.accountUser;
    if (!accountUser?.id) return;

    if (nodes.has(accountUser.id)) {
      nodes.set(accountUser.id, {
        ...nodes.get(accountUser.id)!,
        licenseType: accountUser.licenseType,
      });
      return;
    }

    netNew.add(accountUser.id);
    nodes.set(accountUser.id, {
      ...accountUser,
      __typename: 'SuggestedAccountUser',
      id: accountUser.id,
      profileImage: accountUser.profileImageUrl,
    } as SuggestedAccountUser);
  });

  return {
    ...existingData,
    suggestedMembers: {
      ...existingData.suggestedMembers,
      nodes: Array.from(nodes.values()),
      totalCount: existingData.suggestedMembers.totalCount + netNew.size,
    },
  };
};

export const updateSuggestedAccountUsersCache = (
  cache: ApolloCache<any>,
  memberships: Array<CompleteMembershipFragment | null>,
  resourceId: string
) => {
  updateQuery<GetSuggestedMembersQuery, GetSuggestedMembersQueryVariables>(
    cache,
    {
      query: GetSuggestedMembers,
      variables: {
        filter: { query: '' },
        resourceId,
        orderBy: [
          {
            field: 'NAME',
            direction: 'ASC_NULLS_LAST',
          },
        ],
        page: {
          first: DEFAULT_INITIAL_PAGE_SIZE,
        },
      },
    },
    (existingData) => updateSuggestedAccountUsersData(memberships, existingData)
  );
};
